// modules/reporteFacturas/pages/ReporteFacturasDetalleInternoPage.jsx
// Reporte del DETALLE INTERNO de las facturas / pre-facturas. Cada concepto interno (ej. AVL_NACIONAL) se reparte en líneas fiscales
// con porcentaje (ej. 70 % alquiler + 30 % SVA). Aquí se suma solo la línea de % ALTO de cada concepto; lo de % bajo no entra al total,
// pero se muestra aparte ("No incluido") para que se vea qué se dejó fuera.
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, InputAdornment, MenuItem, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import * as XLSX from "xlsx";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { useAuth } from "../../../context/AuthContext";
import { fieldSx } from "../../egresos/components/formStyles";
import { getReporteDetalleInterno } from "../services/reporteFacturasService";
import { getServiciosFacturacion } from "../../facturacion/services/facturacionService";

const MESES = [
  { value: "Todo", label: "Todo (año completo)" },
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" }, { value: 3, label: "Marzo" }, { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" }, { value: 6, label: "Junio" }, { value: 7, label: "Julio" }, { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" }, { value: 10, label: "Octubre" }, { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" },
];
const SUCURSALES = ["La Paz", "Cochabamba", "Santa Cruz", "Todo"];
const TIPOS = [
  { value: "todos", label: "Facturas y pre-facturas" },
  { value: "no", label: "Solo facturas" },
  { value: "si", label: "Solo pre-facturas" },
];
const anioActual = new Date().getFullYear();
const ANIOS = Array.from({ length: 6 }, (_, i) => anioActual + 1 - i);

const normalizarSucursal = (value) => {
  const s = String(value || "").trim().toLowerCase();
  if (s.includes("santa cruz")) return "Santa Cruz";
  if (s.includes("cochabamba")) return "Cochabamba";
  if (s.includes("la paz") || s.includes("el alto")) return "La Paz";
  return "Todo";
};

const moneda = (v) => Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct = (n) => (n === null || n === undefined ? "?" : `${Number.isInteger(n) ? n : Number(Number(n).toFixed(2))} %`);
const fecha = (v) => { const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]}` : "-"; };

const cellSx = { py: 0.4, px: 0.9, fontSize: "0.76rem", verticalAlign: "middle" };
const headSx = {
  ...cellSx, fontWeight: 800, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: 0.4, color: "text.secondary", whiteSpace: "nowrap",
  bgcolor: "background.paper", borderBottom: 2, borderColor: "divider",
};

const Resumen = ({ etiqueta, valor, color, nota }) => (
  <Box sx={{ flex: "1 1 190px", minWidth: 180, px: 1.5, py: 0.9, border: 1, borderColor: "divider", borderLeft: 3, borderLeftColor: color, bgcolor: "background.paper" }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>{etiqueta}</Typography>
    <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.25, fontVariantNumeric: "tabular-nums" }}>{valor}</Typography>
    {nota && <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{nota}</Typography>}
  </Box>
);

const ReporteFacturasDetalleInternoPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [sucursal, setSucursal] = useState(() => searchParams.get("sucursal") || normalizarSucursal(user?.sucursal));
  const [mes, setMes] = useState(() => { const m = searchParams.get("mes"); return !m ? new Date().getMonth() + 1 : m === "Todo" ? "Todo" : Number(m); });
  const [year, setYear] = useState(() => Number(searchParams.get("year")) || anioActual);
  const [preFac, setPreFac] = useState(() => (["todos", "no", "si"].includes(searchParams.get("preFac")) ? searchParams.get("preFac") : "todos"));
  const [codServicio, setCodServicio] = useState("Todos");
  const [servicios, setServicios] = useState([]);
  const [incluirAnuladas, setIncluirAnuladas] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filtroConcepto, setFiltroConcepto] = useState("");
  const [filtroServicio, setFiltroServicio] = useState("");
  const [agrupar, setAgrupar] = useState("concepto"); // concepto | servicio
  const [busqueda, setBusqueda] = useState("");
  const [pagina, setPagina] = useState(0);
  const [limite, setLimite] = useState(50);

  const buscar = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getReporteDetalleInterno({ sucursal, year, mes, preFac, incluirAnuladas: incluirAnuladas ? 1 : 0, codServicio: codServicio === "Todos" ? undefined : codServicio });
      setData(res.data?.items ? res.data : res.data?.data || null);
      setFiltroConcepto("");
      setFiltroServicio("");
      setPagina(0);
    } catch (e) {
      setData(null);
      setError(e?.response?.data?.error || "No se pudo generar el reporte.");
    } finally {
      setLoading(false);
    }
  };

  // Al abrir la pantalla se genera el reporte con los filtros que llegan del índice
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { buscar(); }, []);

  // Lista de servicios para el filtro
  useEffect(() => {
    getServiciosFacturacion()
      .then((res) => { const l = res?.data?.data ?? res?.data; setServicios(Array.isArray(l) ? l : []); })
      .catch(() => setServicios([]));
  }, []);

  const items = useMemo(() => data?.items || [], [data]);
  const resumen = data?.resumen || [];
  const resumenServicio = data?.resumen_servicio || [];
  const totales = data?.totales || { conceptos: 0, facturas: 0, monto_concepto: 0, monto_incluido: 0, monto_excluido: 0 };

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return items.filter((i) => {
      if (filtroConcepto && i.concepto !== filtroConcepto) return false;
      if (filtroServicio && i.servicio !== filtroServicio) return false;
      if (!q) return true;
      return [i.cliente, i.concepto, i.descripcion_interna, i.num_fac, i.cod_factura, i.servicio, i.periodo].join(" ").toLowerCase().includes(q);
    });
  }, [items, filtroConcepto, filtroServicio, busqueda]);

  const sumaVisibles = useMemo(() => visibles.filter((i) => !i.anulada).reduce((a, i) => ({ incl: a.incl + i.monto_incluido, excl: a.excl + i.monto_excluido, tot: a.tot + i.monto_concepto }), { incl: 0, excl: 0, tot: 0 }), [visibles]);

  const exportarExcel = () => {
    const hoja1 = resumen.map((r) => ({
      Concepto: r.concepto, Facturas: r.facturas, Conceptos: r.conceptos, "Línea que se suma (% alto)": r.linea_incluida,
      "Total sumado (% alto)": r.monto_incluido, "No incluido (% bajo)": r.monto_excluido, "Total del concepto (100 %)": r.monto_concepto,
    }));
    hoja1.push({ Concepto: "TOTAL", Facturas: totales.facturas, Conceptos: totales.conceptos, "Línea que se suma (% alto)": "", "Total sumado (% alto)": totales.monto_incluido, "No incluido (% bajo)": totales.monto_excluido, "Total del concepto (100 %)": totales.monto_concepto });
    const hojaServ = resumenServicio.map((v) => ({
      Servicio: v.servicio, Documentos: v.facturas, Conceptos: v.conceptos, "Conceptos internos que usa": v.detalle_conceptos.map((c) => c.concepto).join(", "),
      "Total sumado (% alto)": v.monto_incluido, "No incluido (% bajo)": v.monto_excluido, "Total del concepto (100 %)": v.monto_concepto,
    }));
    const hoja2 = visibles.map((i) => ({
      Fecha: fecha(i.fecha), Documento: i.tipo_doc, "N.º factura": i.num_fac || "", "Cód.": i.cod_factura, Anulada: i.anulada ? "Sí" : "", Cliente: i.cliente, Servicio: i.servicio, Sucursal: i.sucursal,
      Concepto: i.concepto, "Descripción interna": i.descripcion_interna, Periodo: i.periodo,
      "Línea sumada": i.incluidas.map((l) => `${pct(l.porcentaje)} ${l.detalle}`).join(" | "),
      "Monto sumado": i.anulada ? 0 : i.monto_incluido,
      "Línea no incluida": i.excluidas.map((l) => `${pct(l.porcentaje)} ${l.detalle}`).join(" | "),
      "Monto no incluido": i.anulada ? 0 : i.monto_excluido,
      "Total del concepto": i.anulada ? 0 : i.monto_concepto,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(hoja1), "Resumen por concepto");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(hojaServ), "Resumen por servicio");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(hoja2), "Detalle");
    XLSX.writeFile(wb, `detalle_interno_${year}_${mes === "Todo" ? "anual" : String(mes).padStart(2, "0")}.xlsx`);
  };

  const periodoTxt = `${mes === "Todo" ? "Año completo" : MESES.find((m) => m.value === mes)?.label} ${year}${codServicio !== "Todos" ? ` · ${servicios.find((sv) => sv.id_servicio === codServicio)?.nombre || ""}` : ""}`;
  const pagItems = visibles.slice(pagina * limite, pagina * limite + limite);

  return (
    <DashboardLayout>
      <PageHeader title="Reporte de Detalle Interno" subtitle="Facturas y pre-facturas con conceptos internos: suma de la línea de % alto" backTo="/reporte-facturas" />
      <Box sx={{ p: 1 }}>
        <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mb: 1, py: 0.25, fontSize: "0.8rem", alignItems: "center" }}>
          Cada concepto interno (ej. <b>AVL_NACIONAL</b>) se reparte en líneas con porcentaje (ej. 70 % alquiler + 30 % SVA). Aquí se <b>suma solo la línea de % alto</b>.
          La línea de % bajo <b>no entra al total</b>, pero aparece en la columna <b>“No incluido”</b> para que se vea qué se dejó fuera. Las facturas anuladas no se suman.
        </Alert>

        {/* ── Filtros ── */}
        <Paper variant="outlined" sx={{ p: 1, mb: 1, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <TextField select size="small" label="Sucursal" value={sucursal} onChange={(e) => setSucursal(e.target.value)} sx={{ ...fieldSx, width: 150 }}>
            {SUCURSALES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Mes" value={mes} onChange={(e) => setMes(e.target.value)} sx={{ ...fieldSx, width: 170 }}>
            {MESES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Año" value={year} onChange={(e) => setYear(Number(e.target.value))} sx={{ ...fieldSx, width: 100 }}>
            {ANIOS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Servicio" value={codServicio} onChange={(e) => setCodServicio(e.target.value)} sx={{ ...fieldSx, width: 170 }}>
            <MenuItem value="Todos">Todos los servicios</MenuItem>
            {servicios.map((sv) => <MenuItem key={sv.id_servicio} value={sv.id_servicio}>{sv.nombre}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Documentos" value={preFac} onChange={(e) => setPreFac(e.target.value)} sx={{ ...fieldSx, width: 210 }}>
            {TIPOS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
          <FormControlLabel
            control={<Checkbox size="small" checked={incluirAnuladas} onChange={(e) => setIncluirAnuladas(e.target.checked)} />}
            label="Mostrar anuladas" sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
          />
          <Button size="small" variant="contained" startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />} onClick={buscar} disabled={loading} sx={{ textTransform: "none", fontWeight: 700 }}>
            Generar
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportarExcel} disabled={!data || !items.length} sx={{ textTransform: "none" }}>
            Exportar Excel
          </Button>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError("")}>{error}</Alert>}

        {data && (
          <>
            {/* ── Totales ── */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
              <Resumen etiqueta="Total sumado (% alto)" valor={`Bs ${moneda(totales.monto_incluido)}`} color="#0b6e6e" nota={`${totales.conceptos} conceptos en ${totales.facturas} documentos · ${periodoTxt}`} />
              <Resumen etiqueta="No incluido (% bajo)" valor={`Bs ${moneda(totales.monto_excluido)}`} color="#c77700" nota="líneas de % bajo, fuera del total" />
              <Resumen etiqueta="Total de los conceptos (100 %)" valor={`Bs ${moneda(totales.monto_concepto)}`} color="#546e7a" nota="sumado + no incluido" />
            </Box>

            {/* ── Resumen por concepto ── */}
            <Paper variant="outlined" sx={{ mb: 1, overflow: "hidden" }}>
              <Box sx={{ px: 1.25, py: 0.6, bgcolor: "action.hover", borderBottom: 1, borderColor: "divider", display: "flex", alignItems: "center", gap: 1, borderLeft: "3px solid", borderLeftColor: "primary.main" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "primary.main" }}>Resumen</Typography>
                <ToggleButtonGroup size="small" exclusive value={agrupar} onChange={(_, v) => v && setAgrupar(v)} sx={{ bgcolor: "background.paper", "& .MuiToggleButton-root": { py: 0.15, px: 1.25, fontSize: 12, textTransform: "none", fontWeight: 700 } }}>
                  <ToggleButton value="concepto">Por concepto</ToggleButton>
                  <ToggleButton value="servicio">Por servicio</ToggleButton>
                </ToggleButtonGroup>
                <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>Toca una fila para ver solo sus facturas abajo</Typography>
              </Box>
              {agrupar === "servicio" ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>Servicio</TableCell>
                      <TableCell sx={headSx} align="right">Documentos</TableCell>
                      <TableCell sx={headSx}>Conceptos internos que usa (total sumado de cada uno)</TableCell>
                      <TableCell sx={headSx} align="right">Total sumado</TableCell>
                      <TableCell sx={headSx} align="right">No incluido (% bajo)</TableCell>
                      <TableCell sx={headSx} align="right">Total del servicio</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resumenServicio.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ ...cellSx, py: 2, color: "text.secondary" }}>No hay facturas con detalle interno en este periodo.</TableCell></TableRow>
                    ) : resumenServicio.map((v) => (
                      <TableRow key={v.servicio} hover onClick={() => { setFiltroServicio(filtroServicio === v.servicio ? "" : v.servicio); setFiltroConcepto(""); setPagina(0); }}
                        sx={{ cursor: "pointer", bgcolor: filtroServicio === v.servicio ? (t) => alpha(t.palette.primary.main, 0.1) : undefined }}>
                        <TableCell sx={{ ...cellSx, fontWeight: 800, fontSize: "0.85rem" }}>{v.servicio}</TableCell>
                        <TableCell sx={cellSx} align="right">{v.facturas}<Typography component="span" sx={{ fontSize: 10.5, color: "text.secondary" }}> · {v.servicios_cliente} servicio{v.servicios_cliente === 1 ? "" : "s"} de cliente</Typography></TableCell>
                        <TableCell sx={{ ...cellSx, maxWidth: 420 }}>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            {v.detalle_conceptos.map((c) => (
                              <Chip key={c.concepto} size="small" variant="outlined" label={`${c.concepto}: ${moneda(c.monto)}`} sx={{ height: 20, fontSize: 11 }} />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }} align="right">{moneda(v.monto_incluido)}</TableCell>
                        <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", color: v.monto_excluido ? "warning.main" : "text.disabled" }} align="right">{v.monto_excluido ? moneda(v.monto_excluido) : "—"}</TableCell>
                        <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", color: "text.secondary" }} align="right">{moneda(v.monto_concepto)}</TableCell>
                      </TableRow>
                    ))}
                    {resumenServicio.length > 0 && (
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell sx={{ ...cellSx, fontWeight: 800 }}>TOTAL</TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: 800 }} align="right">{totales.facturas}</TableCell>
                        <TableCell sx={cellSx} />
                        <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }} align="right">{moneda(totales.monto_incluido)}</TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }} align="right">{moneda(totales.monto_excluido)}</TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }} align="right">{moneda(totales.monto_concepto)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>Concepto</TableCell>
                      <TableCell sx={headSx} align="right">Documentos</TableCell>
                      <TableCell sx={headSx}>Línea que se suma (% alto)</TableCell>
                      <TableCell sx={headSx} align="right">Total sumado</TableCell>
                      <TableCell sx={headSx} align="right">No incluido (% bajo)</TableCell>
                      <TableCell sx={headSx} align="right">Total del concepto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resumen.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ ...cellSx, py: 2, color: "text.secondary" }}>No hay facturas con detalle interno en este periodo.</TableCell></TableRow>
                    ) : resumen.map((r) => (
                      <TableRow key={r.concepto} hover onClick={() => { setFiltroConcepto(filtroConcepto === r.concepto ? "" : r.concepto); setFiltroServicio(""); setPagina(0); }}
                        sx={{ cursor: "pointer", bgcolor: filtroConcepto === r.concepto ? (t) => alpha(t.palette.primary.main, 0.1) : undefined }}>
                        <TableCell sx={{ ...cellSx, fontWeight: 800 }}>{r.concepto}</TableCell>
                        <TableCell sx={cellSx} align="right">{r.facturas}{r.conceptos !== r.facturas ? <Typography component="span" sx={{ fontSize: 10.5, color: "text.secondary" }}> ({r.conceptos} cptos.)</Typography> : null}</TableCell>
                        <TableCell sx={{ ...cellSx, maxWidth: 360 }}>{r.linea_incluida || "—"}</TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }} align="right">{moneda(r.monto_incluido)}</TableCell>
                        <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", color: r.monto_excluido ? "warning.main" : "text.disabled" }} align="right">{r.monto_excluido ? moneda(r.monto_excluido) : "—"}</TableCell>
                        <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", color: "text.secondary" }} align="right">{moneda(r.monto_concepto)}</TableCell>
                      </TableRow>
                    ))}
                    {resumen.length > 0 && (
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell sx={{ ...cellSx, fontWeight: 800 }}>TOTAL</TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: 800 }} align="right">{totales.facturas}</TableCell>
                        <TableCell sx={cellSx} />
                        <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }} align="right">{moneda(totales.monto_incluido)}</TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }} align="right">{moneda(totales.monto_excluido)}</TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }} align="right">{moneda(totales.monto_concepto)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </Paper>

            {/* ── Detalle por factura ── */}
            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Box sx={{ px: 1.25, py: 0.6, bgcolor: "action.hover", borderBottom: 1, borderColor: "divider", display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", borderLeft: "3px solid", borderLeftColor: "primary.main" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "primary.main" }}>Detalle por factura</Typography>
                <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{visibles.length} concepto{visibles.length === 1 ? "" : "s"}</Typography>
                {filtroServicio && (
                  <Chip size="small" color="primary" label={`Servicio: ${filtroServicio}`} onDelete={() => { setFiltroServicio(""); setPagina(0); }} deleteIcon={<FilterAltOffIcon />} sx={{ height: 22, fontWeight: 700 }} />
                )}
                {filtroConcepto && (
                  <Chip size="small" color="primary" label={filtroConcepto} onDelete={() => { setFiltroConcepto(""); setPagina(0); }} deleteIcon={<FilterAltOffIcon />} sx={{ height: 22, fontWeight: 700 }} />
                )}
                <Box sx={{ flex: 1 }} />
                <TextField
                  size="small" placeholder="Buscar cliente, concepto o N.º…" value={busqueda} onChange={(e) => { setBusqueda(e.target.value); setPagina(0); }}
                  sx={{ ...fieldSx, width: { xs: "100%", sm: 280 } }}
                  slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
                />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>Fecha</TableCell>
                      <TableCell sx={headSx}>Documento</TableCell>
                      <TableCell sx={headSx}>Cliente</TableCell>
                      <TableCell sx={headSx}>Concepto</TableCell>
                      <TableCell sx={headSx}>Línea sumada (% alto)</TableCell>
                      <TableCell sx={headSx} align="right">Monto sumado</TableCell>
                      <TableCell sx={headSx}>No incluido (% bajo)</TableCell>
                      <TableCell sx={headSx} align="right">Total concepto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagItems.length === 0 ? (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ ...cellSx, py: 3, color: "text.secondary" }}>Ningún concepto coincide con el filtro.</TableCell></TableRow>
                    ) : pagItems.map((i) => (
                      <TableRow key={i.id_grupo} hover sx={{ opacity: i.anulada ? 0.55 : 1, textDecoration: i.anulada ? "line-through" : "none" }}>
                        <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>{fecha(i.fecha)}</TableCell>
                        <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                          <Chip size="small" variant={i.tipo_doc === "Factura" ? "filled" : "outlined"} color={i.tipo_doc === "Factura" ? "success" : "warning"} label={i.tipo_doc === "Factura" ? `Factura ${i.num_fac || ""}`.trim() : `Pre-fac. ${i.cod_factura}`} sx={{ height: 20, fontSize: 11 }} />
                          {i.anulada && <Chip size="small" color="error" label="Anulada" sx={{ height: 18, fontSize: 10, ml: 0.5 }} />}
                        </TableCell>
                        <TableCell sx={{ ...cellSx, maxWidth: 230 }}>
                          <Typography sx={{ fontSize: "0.76rem", fontWeight: 700, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={i.cliente}>{i.cliente}</Typography>
                          <Typography sx={{ fontSize: "0.68rem", color: "text.secondary" }}>{i.servicio} · #{i.cod_servicio_cliente}</Typography>
                        </TableCell>
                        <TableCell sx={cellSx}>
                          <Typography sx={{ fontSize: "0.76rem", fontWeight: 800, lineHeight: 1.2 }}>{i.concepto}</Typography>
                          <Typography sx={{ fontSize: "0.68rem", color: "text.secondary" }}>{i.periodo}{i.cantidad > 1 ? ` · ${i.cantidad} unid.` : ""}</Typography>
                        </TableCell>
                        <TableCell sx={{ ...cellSx, maxWidth: 280 }}>
                          {i.incluidas.map((l, k) => (
                            <Box key={k} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Chip size="small" color="primary" label={pct(l.porcentaje)} sx={{ height: 18, fontSize: 10.5, fontWeight: 800 }} />
                              <Typography sx={{ fontSize: "0.72rem", lineHeight: 1.2 }}>{l.detalle}</Typography>
                            </Box>
                          ))}
                          {i.con_empate && <Typography sx={{ fontSize: "0.66rem", color: "warning.main" }}>Empate de % alto: se suman las dos</Typography>}
                        </TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }} align="right">{moneda(i.monto_incluido)}</TableCell>
                        <TableCell sx={{ ...cellSx, maxWidth: 260 }}>
                          {i.excluidas.length === 0 ? <Typography component="span" sx={{ color: "text.disabled" }}>—</Typography> : i.excluidas.map((l, k) => (
                            <Tooltip key={k} title="Línea de % bajo: no entra al total sumado">
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "warning.dark" }}>
                                <Chip size="small" variant="outlined" color="warning" label={pct(l.porcentaje)} sx={{ height: 18, fontSize: 10.5 }} />
                                <Typography sx={{ fontSize: "0.72rem", lineHeight: 1.2 }}>{l.detalle} · <b>{moneda(l.monto)}</b></Typography>
                              </Box>
                            </Tooltip>
                          ))}
                        </TableCell>
                        <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", color: "text.secondary" }} align="right">{moneda(i.monto_concepto)}</TableCell>
                      </TableRow>
                    ))}
                    {visibles.length > 0 && (
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell colSpan={5} sx={{ ...cellSx, fontWeight: 800 }} align="right">Total de lo filtrado ({visibles.length}):</TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }} align="right">{moneda(sumaVisibles.incl)}</TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: 800, color: "warning.dark" }}>No incluido: {moneda(sumaVisibles.excl)}</TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }} align="right">{moneda(sumaVisibles.tot)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div" count={visibles.length} page={pagina} rowsPerPage={limite} rowsPerPageOptions={[25, 50, 100, 200]}
                onPageChange={(_, p) => setPagina(p)} onRowsPerPageChange={(e) => { setLimite(Number(e.target.value)); setPagina(0); }}
                labelRowsPerPage="Filas" labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{ borderTop: 1, borderColor: "divider", "& .MuiTablePagination-toolbar": { minHeight: 40 } }}
              />
            </Paper>
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ReporteFacturasDetalleInternoPage;
